import { ReactElement, useState } from 'react';
import { Subscription, OrderContext, SubscriptionStatus } from '@localstack/types';
import {
  CounterInput,
  whatIsTheStatus,
  BrightnessAlertFilled,
  Lightbulb,
  ButtonWithDialog,
  WarningContainer,
} from '@localstack/ui';
import {
  computeNumLicensesUsed,
  formatDateTo,
  formatMonetaryAmount,
  getPlanEstimations,
  isDarkMode,
  OrganizationsService,
  SubscriptionService,
  useApiEffect,
  UserService,
  useSnackbar,
} from '@localstack/services';

import { Typography, styled, Stack, Divider, Link, Alert, Box } from '@mui/material';
import { Groups2Outlined } from '@mui/icons-material';
import { ExternalLink } from '@localstack/constants';

import { AppRoute } from '~/config';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { GridComponent } from '../utils';

import { SubscriptionDetailsType } from './SubscriptionDetails';
import { SubscriptionOverviewColumn } from './SubscriptionOverviewColumn';

const SubscriptionInfo = styled('div')(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? theme.palette.background.default : theme.palette.grey[100],
  borderRadius: '5px',
}));

/** Line containing the cost expected based on the number of currently selected seats */
const PriceSummary = ({
  nextInvoiceDate,
  isAnnualPlan,
  seats,
  subscription,
}: {
  nextInvoiceDate: string;
  isAnnualPlan: boolean;
  seats: number;
  subscription: Subscription;
}): JSX.Element => {
  const context: OrderContext = { seats: seats, org_id: '', customer_id: '' };

  const renewalDiscount = subscription.discount_reaches_renewal ? subscription.discount : undefined;

  const newAmount = formatMonetaryAmount(
    getPlanEstimations(subscription.plan.products, context, renewalDiscount),
    subscription.currency,
  );
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body1" color="inherit">
        New {isAnnualPlan ? 'annual' : 'monthly'} total for your next invoice on {nextInvoiceDate}
      </Typography>
      <Typography variant="body1" color="inherit">
        <b>{newAmount}</b>
      </Typography>
    </Stack>
  );
};

interface SubscriptionUpdateMenuItemProps {
  subscription: Subscription;
  subscriptionDetails: SubscriptionDetailsType;
}

export const SubscriptionUpdateMenuItem = ({
  subscription,
  subscriptionDetails,
}: SubscriptionUpdateMenuItemProps): ReactElement => {
  const status = whatIsTheStatus(subscription);
  const [seats, setSeats] = useState(subscription.seats);
  const isAnnualPlan = subscription.plan.interval_months === 12;
  const { userInfo } = useAuthProvider();

  const { updateSubscription, isLoading } = useApiEffect(SubscriptionService, ['updateSubscription'], {
    revalidate: [
      'listSubscriptions',
      'getSubscription',
      'listCiTokens',
      'listInvoices',
      'listPlans',
      'getAggregatedSubscriptionInfo',
    ],
    revalidateOtherClient: {
      client: [OrganizationsService, UserService],
      methods: ['listLicenseAssignments', 'listLicenseAssignmentsForUser', 'getUser'],
    },
  });

  const { showSnackbar } = useSnackbar();

  const context: OrderContext = {
    seats,
    org_id: '',
    customer_id: '',
    interval: subscription.interval_months,
    name: subscription?.name,
    acquisition_channel: subscription?.acquisition_channel,
    billing_channel: subscription?.billing_channel,
  };

  const handlePurchase = async () => {
    const result = await updateSubscription(subscription.id, { plan: subscription.plan.id, ...context });

    if ([SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(result.status)) {
      showSnackbar({
        message: 'Seats updated successfully',
        severity: 'success',
      });
    } else {
      showSnackbar({
        message: 'Something went wrong whilst updating your seats',
        severity: 'error',
      });
    }
  };

  const licensesUsed = (): number | undefined => {
    if (!userInfo || !subscription || !subscriptionDetails.licenseAssignments) {
      return undefined;
    }

    return computeNumLicensesUsed(subscription, subscriptionDetails.licenseAssignments);
  };

  return (
    <>
      <ButtonWithDialog
        componentType="MenuItem"
        dialogTitle="Update subscription seats"
        confirmButtonText="Confirm changes"
        confirmButtonProps={{
          loading: isLoading,
        }}
        onClick={() => handlePurchase()}
        dialogContent={
          <Stack spacing={2} pt={1}>
            <SubscriptionInfo>
              <GridComponent columns="1fr max-content">
                <SubscriptionOverviewColumn
                  subscription={subscription}
                  status={status}
                  emulators={[subscription.plan.emulator]}
                />
                <Stack direction="column" spacing="4px" alignItems="end">
                  <Typography color="text.secondary">Current seats</Typography>
                  <Typography color="text.primary">{subscription.seats}</Typography>
                </Stack>
              </GridComponent>
            </SubscriptionInfo>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body1">
                  <b>Seats for the next renewal, billed {isAnnualPlan ? 'annually' : 'monthly'}.</b>
                </Typography>
                {subscription.plan.updatable && subscription.plan.plan_seat_limit && (
                  <Typography variant="caption">
                    For more than {subscription.plan.plan_seat_limit} users, upgrade the plan or{' '}
                    <Link href={ExternalLink.CONTACT_TEAM} color="inherit">
                      reach out
                    </Link>{' '}
                    to our Sales Team.
                  </Typography>
                )}
              </Box>
              <CounterInput
                value={seats}
                onSetValue={(value) => setSeats(Math.max(1, value))}
                onIncrement={() => setSeats(seats + 1)}
                onDecrement={() => (seats > 1 ? setSeats(seats - 1) : null)}
                min={licensesUsed()}
                max={subscription.plan.plan_seat_limit ?? Infinity}
                ariaLabelledBy="seats-label"
              />
            </Stack>
            {licensesUsed() === seats && (
              <WarningContainer icon={<BrightnessAlertFilled color="inherit" />}>
                <Typography variant="body1" color="inherit">
                  To reduce seats, you must unassign{' '}
                  <Link color="inherit" href={AppRoute.SETTINGS_MEMBERS}>
                    licenses
                  </Link>{' '}
                  first.
                </Typography>
              </WarningContainer>
            )}

            <Divider />

            <PriceSummary
              isAnnualPlan={isAnnualPlan}
              nextInvoiceDate={formatDateTo(subscription.current_period_end, 'MMM, DD, YYYY')}
              seats={seats}
              subscription={subscription}
            />
            <Alert severity="info" icon={<Lightbulb />}>
              <Typography variant="body1">
                Seat changes apply immediately after confirmation. Billing updates take effect at your next renewal.
              </Typography>
            </Alert>
          </Stack>
        }
      >
        <Groups2Outlined fontSize="small" />
        <Typography variant="inherit" sx={{ marginLeft: '0.5em' }}>
          Update seats
        </Typography>
      </ButtonWithDialog>
    </>
  );
};
